import { Box, Tooltip, Typography } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import PropTypes from 'prop-types'; // Import PropTypes for prop validation
import { useRef } from 'react';
import type { BarProps } from 'recharts'; // Correct type-only import
import {
  Bar,
  ComposedChart,
  Line,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import {
  calculateMonthDifference,
  categoryMapping,
} from '../Function/helper.function';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

// Define the shape of data items
interface GraphDataItem {
  month: number;
  cumulative_assets: number;
  green_yellow: number;
  yellow_red: number;
  work_order_age_in_days: number;
}

// Define types for CustomTooltip props
interface CustomTooltipProps {
  active?: boolean;
  payload?: any;
  label?: string;
  enableDarkTheme: boolean;
}

// Custom Tooltip component
const CustomTooltip = ({
  active,
  payload,
  label,
  enableDarkTheme,
}: CustomTooltipProps) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: enableDarkTheme
            ? palette.pageBackground
            : palette.pageBackground,
          padding: '10px',
        }}
      >
        <Typography
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.grey
              : ThemePalette.typography.black,
          }}
          variant="body2"
        >
          {Number(payload[0].value).toFixed(0)}
          {t('assets_to_check')}
        </Typography>
        <Typography
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.grey
              : ThemePalette.typography.black,
          }}
          variant="body2"
        >
          in {label} {t('month_s')}
        </Typography>
      </Box>
    );
  }

  return null;
};

// Function to determine the color based on the month
const getBarColor = (
  month: number,
  greenYellow: number,
  yellowRed: number,
  workOrderInDays: number
) => {
  const monthsToDays = (month: number) => {
    if (month === 0) return 29;
    const AVERAGE_DAYS_PER_MONTH = 30.44;
    return Math.round(month * AVERAGE_DAYS_PER_MONTH);
  };

  const monthInDays = monthsToDays(month);
  if (greenYellow && yellowRed && monthInDays) {
    if (monthInDays < greenYellow) {
      return categoryMapping.low.color;
    } else if (monthInDays >= greenYellow && monthInDays <= yellowRed) {
      return categoryMapping.med.color;
    } else if (monthInDays > yellowRed) {
      return categoryMapping.high.color;
    }
  }

  if (month <= 1.5) {
    return categoryMapping.low.color;
  } else if (month >= 2 && month <= 3) {
    return categoryMapping.med.color;
  } else {
    return categoryMapping.high.color;
  }
};

// Define the props for CustomBarWithArrow
interface CustomBarWithArrowProps extends BarProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload: GraphDataItem;
  installationMonth: number;

  pointerColor: string;
}

// Custom Bar Component with a Flipped Arrow and Gradient
const CustomBarWithArrow: React.FC<CustomBarWithArrowProps> = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  payload,
  installationMonth,
  pointerColor,
}) => {
  const { t } = useTranslation();
  // Check if the current bar corresponds to the installation month
  const isInstallationMonth = payload.month === installationMonth;
  const barColor = getBarColor(
    payload.month,
    payload.green_yellow,
    payload.yellow_red,
    payload.work_order_age_in_days
  );
  const gradientId = `gradient-${payload.month}-${Math.random()
    .toString(36)
    .substr(2, 9)}`;

  return (
    <g>
      {/* Bar with gradient */}
      <defs>
        <linearGradient id={gradientId} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor={barColor} stopOpacity={1} />
          <stop offset="100%" stopColor={barColor} stopOpacity={1} />
        </linearGradient>
      </defs>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={`url(#${gradientId})`}
      />
      {/* Flipped Arrow only on the bar corresponding to the installation month */}
      {isInstallationMonth && (
        <Tooltip title={t('this_represents_the_current_month')}>
          <polygon
            points={`${x + width / 2 - 5},${y + height + -90} 
                  ${x + width / 2 + 5},${y + height + -90} 
                  ${x + width / 2},${y + height + -80}`}
            fill={pointerColor}
          />
        </Tooltip>
      )}
    </g>
  );
};

// Add PropTypes validation
CustomBarWithArrow.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  payload: PropTypes.shape({
    month: PropTypes.number.isRequired,
    cumulative_assets: PropTypes.number.isRequired,
    green_yellow: PropTypes.number.isRequired,
    yellow_red: PropTypes.number.isRequired,
    work_order_age_in_days: PropTypes.number.isRequired,
  }).isRequired,
  installationMonth: PropTypes.number.isRequired,
  pointerColor: PropTypes.string.isRequired,
};

export const SurvivalAnalysisBarChart = ({
  generateGraphDataItems,
  component,
  enableDarkTheme,
  pointerColor,
}: {
  generateGraphDataItems: GraphDataItem[];
  component: any;
  enableDarkTheme: boolean;
  pointerColor: string;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const containerRef = useRef<HTMLDivElement>(null);

  // Find the maximum cumulative_assets value
  const maxCumulativeAssets = Math.max(
    ...generateGraphDataItems.map((item) => item.cumulative_assets)
  );

  // Scale the cumulative_assets to percentages
  const scaledData = generateGraphDataItems.map((item) => ({
    ...item,
    cumulative_assets: (item.cumulative_assets / maxCumulativeAssets) * 100,
    green_yellow: component.green_yellow ?? 0,
    yellow_red: component.yellow_red ?? 0,
    work_order_age_in_days: component.work_order_age_in_days,
  }));

  // Convert the date_of_orders from string to Date
  const installationDate = component.date_of_orders;
  const installationMonth = calculateMonthDifference(
    installationDate,
    new Date()
  );

  return (
    <Box
      ref={containerRef}
      sx={{ width: '100%', height: '100%', position: 'relative' }}
    >
      <ResponsiveContainer width="100%" height="75%">
        <ComposedChart
          data={scaledData}
          margin={{
            top: 20,
            bottom: 40, // Increased bottom margin for X-axis label
          }}
        >
          <XAxis
            dataKey="month"
            tickLine={false}
            stroke={palette.textPrimary}
            label={{
              value: 'Month',
              position: 'bottom',
              offset: -5, // Adjusted offset for the label position
              fill: palette.textPrimary,
              fontSize: 12,
            }}
          />
          <YAxis
            stroke={palette.textPrimary}
            domain={[0, 100]}
            tickFormatter={(tick) => `${tick}%`}
          />
          <RechartsTooltip
            content={<CustomTooltip enableDarkTheme={enableDarkTheme} />}
          />
          <Bar
            dataKey="cumulative_assets"
            barSize={10}
            isAnimationActive={false}
            shape={(props: any) => (
              <CustomBarWithArrow
                {...(props as CustomBarWithArrowProps)}
                installationMonth={installationMonth}
                pointerColor={pointerColor}
              />
            )}
          />
          <Line
            type="monotone"
            dataKey="cumulative_assets"
            stroke={enableDarkTheme ? '#8884d8' : '#000000'}
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};
